import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import CorgeeModal from './CorgeeModal';
import type { CorgeeModalRef } from './CorgeeModal';
import type { Attachment } from '../../types/conversation';
import type { ApiConfig, CorgeeFunctions } from '../ConversationItem/types';
import { useVideoDecodeFallback } from './useVideoDecodeFallback';
import VideoTranscodeOverlay from './VideoTranscodeOverlay';
import { fetchAttachmentBytes } from '../../services/imagesGalleryService';
import './VideoModal.css';

export interface VideoModalRef {
  openModal: (callback?: () => void) => void;
  closeModal: () => void;
}

export interface VideoModalProps {
  openModal?: boolean;
  url: string;
  playing?: boolean;
  controls?: boolean;
  attachments: Attachment | Attachment[];
  onFileDownloadVideo:
    | ((
        e: React.MouseEvent,
        attachment: Attachment,
        api: ApiConfig,
        corgeeFunctions?: CorgeeFunctions
      ) => void)
    | string;
  title?: string;
  corgeeFunctions?: CorgeeFunctions;
  api: ApiConfig;
  onClose?: () => void;
  isDraggable?: boolean;
  closeOnEscape?: boolean;
  height?: number;
  width?: number;
  hasMaxHeight?: boolean;
  allowSaveAndNewCheckBox?: boolean;
  hideActionButtons?: boolean;
  className?: string;
}

const VideoModal = forwardRef<VideoModalRef, VideoModalProps>(
  (
    {
      openModal = false,
      url,
      playing = false,
      controls = true,
      attachments,
      onFileDownloadVideo,
      title = '',
      corgeeFunctions,
      api,
      onClose,
      isDraggable = false,
      closeOnEscape = false,
      width = 680,
      hasMaxHeight = false,
      hideActionButtons = true,
      className = '',
    },
    ref,
  ) =>
  {
    const [isPlaying, setIsPlaying] = useState<boolean>(playing);

    const videoRef = useRef<HTMLVideoElement>(null);
    const corgeeModalRef = useRef<CorgeeModalRef>(null);

    // Swaps in an H.264 copy when the browser cannot decode the original.
    // The source is read through the API: storage does not allow CORS.
    const sourceAttachment = Array.isArray(attachments) ? attachments[0] : attachments;
    const loadSource = sourceAttachment && (sourceAttachment.AttachmentId > 0 || Number(sourceAttachment.EmbeddedImageId) > 0)
      ? () => fetchAttachmentBytes(sourceAttachment.AttachmentId, sourceAttachment.EmbeddedImageId)
      : undefined;
    const { src, transcodeProgress, transcodeFailed, handlers: decodeHandlers } =
      useVideoDecodeFallback(url, videoRef, loadSource);

    useImperativeHandle(ref, () => ({
      openModal: (callback?: () => void) =>
      {
        corgeeModalRef.current?.openModal(callback);
      },
      closeModal: () =>
      {
        corgeeModalRef.current?.closeModal();
      },
    }));

    // Sync playing state with prop
    useEffect(() =>
    {
      setIsPlaying(playing);
    }, [playing]);

    // Control video element play/pause based on isPlaying state
    useEffect(() =>
    {
      if (!videoRef.current) return;
      if (isPlaying)
      {
        videoRef.current.play().catch(() =>
        {
          /* autoplay blocked */
        });
      }
      else
      {
        videoRef.current.pause();
      }
    }, [isPlaying, openModal]);

    const handleClose = (): void =>
    {
      // Pause video when closing the modal
      if (videoRef.current)
      {
        videoRef.current.pause();
      }
      setIsPlaying(false);

      if (onClose && typeof onClose === 'function')
      {
        setTimeout(() =>
        {
          onClose();
        }, 250);
      }
    };

    const handleOpenInNewWindow = (e: React.MouseEvent): void =>
    {
      e.preventDefault();
      const attachment = Array.isArray(attachments) ? attachments[0] : attachments;
      if (attachment)
      {
        window.open(
          `${window.location.origin}/Dashboard/FileVideo/#/${attachment.FileName}`,
          '_blank',
        );
      }
      // Pause the current video
      if (videoRef.current)
      {
        videoRef.current.pause();
      }
      setIsPlaying(false);
    };

    const handleDownload = (e: React.MouseEvent): void =>
    {
      e.preventDefault();
      const attachment = Array.isArray(attachments) ? attachments[0] : attachments;
      if (attachment && typeof onFileDownloadVideo === 'function')
      {
        onFileDownloadVideo(e, attachment, api, corgeeFunctions);
      }
    };

    const attachment = Array.isArray(attachments) ? attachments[0] : attachments;

    // "Open in new tab" icon rendered via headerExtra
    const headerExtra = (
      <a
        id="NewTabVideoLink"
        href="#"
        title="Open in a new tab"
        className="video-modal__open-new-tab"
        onClick={handleOpenInNewWindow}
      >
        <i id="NewTabVideo" className="material-icons">
          open_in_new
        </i>
      </a>
    );

    return (
      <CorgeeModal
        ref={corgeeModalRef}
        className={`video-modal ${className}`}
        openModal={openModal}
        title={title}
        headerExtra={headerExtra}
        onClose={handleClose}
        isDraggable={isDraggable}
        closeOnEscape={closeOnEscape}
        width={width}
        hasMaxHeight={hasMaxHeight}
        hideActionButtons={hideActionButtons}
      >
        <div className="video-modal__player-wrapper">
          <video
            ref={videoRef}
            className="video-modal__player"
            src={src}
            controls={controls && !transcodeProgress && !transcodeFailed}
            controlsList="nodownload"
            disablePictureInPicture
            onContextMenu={e => e.preventDefault()}
            autoPlay={playing}
            {...decodeHandlers}
          />

          <VideoTranscodeOverlay
            transcodeProgress={transcodeProgress}
            transcodeFailed={transcodeFailed}
          />
        </div>

        <div className="video-modal__footer">
          <h4 className="video-modal__description">{attachment?.Description || ''}</h4>
          <a className="video-modal__download-link" href="#" onClick={handleDownload}>
            Download
          </a>
        </div>
      </CorgeeModal>
    );
  },
);

VideoModal.displayName = 'VideoModal';

export default VideoModal;
